import type { GSplatRevealShowcaseEffectOptions } from './GSplatRevealShowcaseEffect'
import { GSplatRevealShowcaseEffect } from './GSplatRevealShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const DEFAULT_UNROLL_MIN_PIXEL_SIZE = 0.5
const MIN_UNROLL_PIXEL_SIZE = 0.02

const unrollShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uMotionScale;
uniform float uDuration;
uniform vec2 uHeightRange;
uniform float uEffectScale;
uniform vec3 uCenter;

${SHOWCASE_SHADER_COMMON_GLSL}

float revealUnrollRevealTime() {
  return clamp(uTime * uTimeScale / uDuration, 0.0, 1.0) * 6.0;
}

float revealUnrollMotionTime() {
  return uTime * uTimeScale;
}

vec3 revealUnrollToSourceSpace(vec3 local) {
  vec2 horizontal = local.xy;
  return vec3(horizontal.x, -local.z, horizontal.y);
}

vec3 revealUnrollFromSourceSpace(vec3 local) {
  return vec3(local.x, local.z, -local.y);
}

float revealUnrollCenterFactor(float t) {
  return 1.0 - exp(-t) * 2.0 * max(uMotionScale, 0.0);
}

float revealUnrollScaleFactor(float t, float height) {
  return smoothstep(0.3, 0.7, t + height - 2.0);
}

float revealUnrollNormalizeHeight(float height) {
  float normalized = (height - uHeightRange.x) / (uHeightRange.y - uHeightRange.x);
  return mix(-0.5, 0.5, clamp(normalized, 0.0, 1.0));
}

float revealUnrollOriginalHeight(vec3 sourceLocal, float t) {
  float centerFactor = revealUnrollCenterFactor(t);
  float safeFactor = abs(centerFactor) > 0.0001 ? centerFactor : 0.0001;
  return sourceLocal.y / safeFactor;
}

float revealUnrollSpatialScale() {
  return max(uEffectScale, 0.001);
}

float revealUnrollDetailScale() {
  float spatialScale = revealUnrollSpatialScale();
  return spatialScale * spatialScale;
}

void modifySplatCenter(inout vec3 center) {
  vec3 local = center - uCenter;
  vec3 sourceLocal = revealUnrollToSourceSpace(local);
  float t = revealUnrollMotionTime();
  float twist = (sourceLocal.y * 50.0 - 20.0)
    * exp(-t)
    * max(uMotionScale, 0.0);

  sourceLocal.xz = showcaseRotate2d(sourceLocal.xz, twist);
  center = uCenter + revealUnrollFromSourceSpace(sourceLocal * revealUnrollCenterFactor(t));
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  vec3 sourceLocal = revealUnrollToSourceSpace(originalCenter - uCenter);
  float t = revealUnrollRevealTime();
  float visible = revealUnrollScaleFactor(t, revealUnrollNormalizeHeight(sourceLocal.y));
  float detailScale = revealUnrollDetailScale();
  float recoveryScale = clamp(
    abs(revealUnrollCenterFactor(revealUnrollMotionTime())),
    0.0,
    1.0
  );
  vec3 tinyA = covA;
  vec3 tinyB = covB;
  vec3 recoveredA = covA;
  vec3 recoveredB = covB;

  gsplatMakeRound(tinyA, tinyB, 0.002 * detailScale);
  gsplatApplyUniformScale(recoveredA, recoveredB, recoveryScale);
  covA = mix(tinyA, recoveredA, visible);
  covB = mix(tinyB, recoveredB, visible);
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = center - uCenter;
  vec3 sourceLocal = revealUnrollToSourceSpace(local);
  float revealTime = revealUnrollRevealTime();
  float height = revealUnrollNormalizeHeight(
    revealUnrollOriginalHeight(sourceLocal, revealUnrollMotionTime())
  );

  color *= step(0.0, revealTime * 0.5 + height - 0.5);
}
`

const unrollShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn revealUnrollRevealTime() -> f32 {
  return clamp(uniform.uTime * uniform.uTimeScale / uniform.uDuration, 0.0, 1.0) * 6.0;
}

fn revealUnrollMotionTime() -> f32 {
  return uniform.uTime * uniform.uTimeScale;
}

fn revealUnrollCenterFactor(t: f32) -> f32 {
  return 1.0 - exp(-t) * 2.0 * max(uniform.uMotionScale, 0.0);
}

fn revealUnrollNormalizeHeight(height: f32) -> f32 {
  let normalized = (height - uniform.uHeightRange.x) /
    (uniform.uHeightRange.y - uniform.uHeightRange.x);
  return mix(-0.5, 0.5, clamp(normalized, 0.0, 1.0));
}

fn revealUnrollOriginalHeight(sourceLocal: vec3f, t: f32) -> f32 {
  let centerFactor = revealUnrollCenterFactor(t);
  let safeFactor = select(0.0001, centerFactor, abs(centerFactor) > 0.0001);
  return sourceLocal.y / safeFactor;
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = *center - uniform.uCenter;
  var sourceLocal = vec3f(local.x, -local.z, local.y);
  let t = revealUnrollMotionTime();
  let twist = (sourceLocal.y * 50.0 - 20.0) * exp(-t) * max(uniform.uMotionScale, 0.0);
  sourceLocal.xz = showcaseRotate2d(sourceLocal.xz, twist);
  sourceLocal = sourceLocal * revealUnrollCenterFactor(t);
  *center = uniform.uCenter + vec3f(sourceLocal.x, sourceLocal.z, -sourceLocal.y);
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let sourceLocal = vec3f(
    originalCenter.x - uniform.uCenter.x,
    originalCenter.y - uniform.uCenter.y,
    -(originalCenter.z - uniform.uCenter.z)
  );
  let visible = smoothstep(
    0.3,
    0.7,
    revealUnrollRevealTime() + revealUnrollNormalizeHeight(sourceLocal.y) - 2.0
  );
  let detailScale = max(uniform.uEffectScale, 0.001);
  let recoveryScale = clamp(abs(revealUnrollCenterFactor(revealUnrollMotionTime())), 0.0, 1.0);
  *scale = mix(vec3f(0.002 * detailScale * detailScale), (*scale) * recoveryScale, visible);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = center - uniform.uCenter;
  let sourceLocal = vec3f(local.x, local.y, -(local.z));
  let height = revealUnrollNormalizeHeight(
    revealUnrollOriginalHeight(sourceLocal, revealUnrollMotionTime())
  );
  *color = *color * step(0.0, revealUnrollRevealTime() * 0.5 + height - 0.5);
}
`

const DEFAULT_HEIGHT_RANGE: readonly [number, number] = [-0.5, 0.5]

export interface GSplatRevealUnrollEffectOptions extends GSplatRevealShowcaseEffectOptions {
  heightRange?: readonly [number, number]
  effectScale?: number
  minPixelSize?: number
}

export class GSplatRevealUnrollEffect extends GSplatRevealShowcaseEffect {
  private readonly _heightRange: readonly [number, number]
  private readonly _effectScale: number
  private readonly _minPixelSize: number

  constructor(options: GSplatRevealUnrollEffectOptions = {}) {
    super(
      {
        id: 'reveal-unroll',
        shader: unrollShaderGLSL,
        shaderWGSL: unrollShaderWGSL,
      },
      options
    )

    this._heightRange = normalizeHeightRange(options.heightRange)
    this._effectScale = normalizeEffectScale(options.effectScale)
    const detailScale = this._effectScale * this._effectScale
    this._minPixelSize =
      options.minPixelSize ??
      Math.max(MIN_UNROLL_PIXEL_SIZE, DEFAULT_UNROLL_MIN_PIXEL_SIZE * detailScale)
    this.uniforms.minPixelSize = { value: this._minPixelSize }
  }

  protected override updateEffect(effectTime: number): void {
    super.updateEffect(effectTime)
    this.setUniform('uHeightRange', this._heightRange)
    this.setUniform('uEffectScale', this._effectScale)
    this.setUniform('minPixelSize', this._minPixelSize)
  }
}

function normalizeHeightRange(
  value: readonly [number, number] | undefined
): readonly [number, number] {
  if (!value || !Number.isFinite(value[0]) || !Number.isFinite(value[1]) || value[1] <= value[0]) {
    return DEFAULT_HEIGHT_RANGE
  }

  return [value[0], value[1]]
}

function normalizeEffectScale(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.min(1, Math.max(value, 0.001))
    : 1
}
